import moment from 'moment'
import Vue from 'vue'
import Utils from '@/utils'
import { API, SportsKind, DataFreshnessKind } from '@/models/core'
import { Enum } from '@/helpers/enum'

export const FootballMatchStatusKind = new Enum({
  Started: 'started',
  NotStarted: 'not_started',
  Completed: 'completed'
})

export const FootballMatchSessionKind = new Enum({
  NotStarted: 'notstarted',
  FirstHalf: 'first_half',
  SecondHalf: 'second_half',
  ExtraTimeFirstHalf: 'extra_time_first_half',
  ExtraTimeSecondHalf: 'extra_time_second_half',
  Penalty: 'penalty'
})

export const FootballMatchTimelineKind = new Enum({
  Start: 'start',
  Break: 'break',
  End: 'end',
  Goal: 'goal',
  Card: 'card',
  Substitution: 'substitution',
  Foul: 'foul',
  Kick: 'kick',
  OffSide: 'offside',
  PrePlay: 'preplay',
  PostPlay: 'postplay',
  EndMatch: 'end_match',
  Comment: 'comment',
  Throw: 'throw',
  HandBall: 'hand_ball',

  PenaltyMiss: 'penalty_miss',
  PenaltyGoal: 'penalty_goal'
})

export const FootballPlayerRole = new Enum({
  Goalkeepers: 'goalkeeper',
  Defenders: 'defender',
  Midfielder: 'midfielder',
  Strikers: 'striker',
  Unknown: 'unknown'
})

export const FootballRoleOrder = [
  { keys: [FootballPlayerRole.Goalkeepers.key], name: 'Goalkeepers' },
  { keys: [FootballPlayerRole.Defenders.key], name: 'Defenders' },
  { keys: [FootballPlayerRole.Midfielder.key], name: 'Midfielder' },
  { keys: [FootballPlayerRole.Strikers.key], name: 'Strikers' },
  { keys: null, name: 'Others' }
]

export class FootballPlayerFantasyPoint {
  static createFromJson (raw) {
    let point = new FootballPlayerFantasyPoint()
    point = Object.assign(point, raw)
    return point
  }
}

export class FootballPlayerFantasyCredit {
  static createFromJson (raw) {
    let point = new FootballPlayerFantasyCredit()
    point = Object.assign(point, raw)
    point.lastUpdated = moment(raw.last_updated_match_date)
    if (point.credit_src === undefined) {
      point.credit_src = 'roanuz'
    }
    return point
  }
}

export class FootballPlayer {
  get groupingRole () {
    return this.role
  }

  get fantasyRole () {
    return this.groupingRole
  }

  static createFromJson (key, raw) {
    let player = new FootballPlayer()
    player = Object.assign(player, raw)
    player.role = FootballPlayerRole.praseSafe(raw.role)
    player.key = key
    return player
  }
}

export class FootballMatchTimelineItem {
  get shouldShow () {
    if (this.event.isStart || this.event.isEnd || this.event.isEndMatch) {
      return false
    }

    if (this.event.isGoal && this.session.isPenalty) {
      return false
    }

    return true
  }

  get eventClass () {
    let cls = {}
    if (this.event.isGoal || this.event.isPenaltyGoal) {
      cls.highlight = true
    }

    if (this.event.isPenaltyMiss || this.event.isRedCard) {
      cls.alert = true
    }

    if (this.event.isSubstitution || this.event.isCard) {
      cls.changing = true
    }
    return cls
  }

  get tagTitle () {
    return Utils.capitalise(this.event.value)
  }

  isForTeam (teamKey) {
    if (this.event.isGoal && this.data.goal_type === 'own_goal') {
      return this.data.team !== teamKey
    }
    return this.data.team === teamKey
  }

  static createFromJson (raw) {
    let item = new FootballMatchTimelineItem()
    item = Object.assign(item, raw)
    item.event = FootballMatchTimelineKind.parse(raw.event)
    item.session = FootballMatchSessionKind.parse(raw.session)

    if (item.event.isKick && item.session.isPenalty) {
      if (item.data.is_goal) {
        item.event = FootballMatchTimelineKind.PenaltyGoal
      } else {
        item.event = FootballMatchTimelineKind.PenaltyMiss
      }
    }

    if (!item.data.team && item.data.committed_team) {
      item.data.team = item.data.committed_team
    }

    return item
  }
}

export class FootballMatchTimelineSession {
  constructor (session) {
    this.session = session
    this.items = []
  }

  static createFromJson (raw) {
    let sessions = []
    let lastItem = null
    let session = null
    raw.reverse()
    raw.forEach((itemRaw) => {
      const item = FootballMatchTimelineItem.createFromJson(itemRaw)
      if (lastItem === null || item.session !== session.session) {
        if (session !== null) {
          sessions.push(session)
        }

        session = new FootballMatchTimelineSession(item.session)
        lastItem = item
      }

      session.items.push(item)
    })

    if (session !== null) {
      sessions.push(session)
    }

    return sessions
  }
}

export class FootballMatch {
  constructor () {
    this.sport = SportsKind.Football
  }

  get dataFreshness () {
    let freshness = DataFreshnessKind.Warm
    if (this.info.status.isStarted) {
      freshness = DataFreshnessKind.Live
    } else if (this.info.status.isCompleted) {
      const delta = Math.abs(this.info.start_at.diff(moment(), 'minutes'))
      if (delta < (90 + 15)) {
        freshness = DataFreshnessKind.Hot
      }
    } else if (this.info.status.isNotStarted) {
      const delta = Math.abs(this.info.start_at.diff(moment(), 'minutes'))
      if (delta < 15) {
        freshness = DataFreshnessKind.Hot
      }
    }
    return freshness
  }

  get requiresLiveUpdate () {
    return this.dataFreshness.isLive || this.dataFreshness.isHot
  }

  get venue () {
    let items = []
    const props = ['name', 'city', 'country']
    props.forEach((key) => {
      const val = this.info.stadium[key]
      if (val) {
        items.push(val)
      }
    })

    return items.join(', ')
  }

  get minuteLabel () {
    if (this.sessions) {
      let minute = 0
      let otherLabel = null
      this.sessions.forEach((session) => {
        otherLabel = null
        if (session.end != null) {
          if (session.key === 'first_half' || session.key === 'second_half') {
            minute += 45
            otherLabel = (session.key === 'first_half') ? 'HT' : null
          } else {
            minute += parseInt(session.length_sec / 60)
          }
        } else {
          const start = moment.unix(session.start)
          const sessionMinute = start.diff(new Date(), 'minutes') * -1
          minute += sessionMinute
        }
      })

      if (otherLabel) {
        return otherLabel
      }
      return `${minute}'`
    } else {
      return ''
    }
  }

  get havePlayersInfo () {
    if (!this.teams) {
      return false
    }

    const teamA = this.teams[this.info.home.key]
    if (teamA.players && teamA.players.length > 0) {
      return true
    }

    const teamB = this.teams[this.info.away.key]
    if (teamB.players && teamB.players.length > 0) {
      return true
    }

    return false
  }

  get shortName () {
    const teamA = this.teams[this.info.home.key]
    const teamB = this.teams[this.info.away.key]
    return `${teamA.code} vs ${teamB.code}`
  }

  teamPlayersByRole (teamKey) {
    const playerList = this.teams[teamKey].players
    return this.groupPlayersByRole(playerList, this.players)
  }

  updateLatest (useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }

    return API.instance().get(`/match/${this.info.key}/`, params).then((resp) => {
      const updatedMatch = FootballMatch.createFromJson(resp.data.data.match)
      this.overwrite(updatedMatch)
    })
  }

  teamShortName (teamIndexkey) {
    let teamKey = teamIndexkey
    if (teamIndexkey === 'a') {
      teamKey = this.info.home.key
    } else if (teamIndexkey === 'b') {
      teamKey = this.info.away.key
    }
    return this.teams[teamKey].code
  }

  overwrite (updatedMatch) {
    Object.entries(updatedMatch).forEach(obj => Vue.set(this, obj[0], obj[1]))
  }

  static createFromJson (raw) {
    raw.info = raw.match
    delete raw.match

    let match = new FootballMatch()
    match = Object.assign(match, raw)
    if (match.info.start_date) {
      match.info.start_at = moment.unix(match.info.start_date.timestamp)
    } else {
      match.info.start_at = moment.unix(match.info.start.timestamp)
    }
    match.start_at = match.info.start_at
    match.slotTime = match.start_at
    match.info.home = match.teams[match.info.home]
    match.info.away = match.teams[match.info.away]
    match.info.status = FootballMatchStatusKind.parse(match.info.status)

    if (match.players) {
      Object.keys(match.players).forEach((key) => {
        match.players[key] = FootballPlayer.createFromJson(key, match.players[key])
      })
    }

    if (!match.timeline) {
      match.timeline = []
    } else {
      match.timeline = FootballMatchTimelineSession.createFromJson(match.timeline)
    }

    if (match.match_result) {
      match.result = match.match_result
    }

    if (match.score) {
      const detail = match.score.detail
      let onboard = { home: 0, away: 0 }
      if (detail.first_half) {
        onboard.home += detail.first_half.home
        onboard.away += detail.first_half.away
      }

      if (detail.second_half) {
        onboard.home += detail.second_half.home
        onboard.away += detail.second_half.away
      }

      detail.onboard = onboard
    }

    return match
  }

  static homePage () {
    return API.instance().get('/').then((resp) => {
      const raw = resp.data.data.matches
      let matches = {}
      Object.keys(raw).forEach(status => {
        matches[status] = []
        raw[status].forEach(rawMatch => {
          matches[status].push(FootballMatch.createFromJson(rawMatch))
        })

        matches[status].sort((x, y) => {
          if (x.info.start_at < y.info.start_at) {
            return -1
          } else if (x.info.start_at > y.info.start_at) {
            return 1
          }
          return 0
        })
      })

      return matches
    })
  }

  static fetch (key, useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }

    return API.instance().get(`/match/${key}/`, params).then((resp) => {
      return FootballMatch.createFromJson(resp.data.data.match)
    })
  }

  groupPlayersByRole (playerKeys, players) {
    let playerRoles = []
    let roleMap = {}
    FootballRoleOrder.forEach((role, index) => {
      playerRoles.push({ name: role.name, players: [] })
      if (role.keys === null) {
        roleMap['others'] = index
      } else {
        role.keys.forEach((key) => { roleMap[key] = index })
      }
    })

    playerKeys.forEach((playerKey) => {
      const player = players[playerKey]
      let role = player.groupingRole
      let index = roleMap['others']

      if (roleMap.hasOwnProperty(role.key)) {
        index = roleMap[role.key]
      }
      playerRoles[index].players.push(player.key)
    })

    return playerRoles
  }

  fantasyPoints (useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }
    return API.instance().get(`/fantasy-match-points/${this.info.key}/`, params).then((resp) => {
      let raw = resp.data.data.fantasy

      const fantasy = raw.fantasy

      if (raw.players) {
        let players = raw.players
        let playerKeys = []

        Object.keys(players).forEach((key) => {
          players[key] = FootballPlayer.createFromJson(key, players[key])
        })

        let points = fantasy.points
        points.forEach((point) => {
          if (players[point.player_key]) {
            playerKeys.push(point.player_key)
            players[point.player_key].fantasyPoints = FootballPlayerFantasyPoint.createFromJson(point)
          } else {
            console.log('Player missing', point.player_key)
          }
        })

        const grouped = this.groupPlayersByRole(playerKeys, players)
        const teams = raw.teams
        const fantasyMetrics = fantasy.metrics

        return { grouped, players, playerKeys, teams, fantasyMetrics }
      }

      return null
    })
  }

  fantasyCredits (useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }
    return API.instance().get(`/fantasy-match-credits/${this.info.key}/`, params).then((resp) => {
      return this.parseCreditResponse(resp)
    })
  }

  footballFantasyCredit (useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader(true)
    }
    return API.instance().get(`/workflow/fantasy-credit-variation/${this.info.key}/?sport=football`, params).then((resp) => {
      return this.parseCreditVariationResponse(resp)
    })
  }

  save (key, players, isFrozen) {
    let frozen = null
    if (isFrozen) {
      frozen = isFrozen
    }
    const postData = {
      payload: players,
      mark_frozen: frozen
    }
    return API.instance().post(`/workflow/fantasy-credit-variation/${key}/?sport=football`, postData).then((resp) => {
      return this.parseCreditVariationResponse(resp)
    })
  }
  parseCreditResponse (resp) {
    const raw = resp.data.data.fantasy
    const fantasy = raw.fantasy
    if (raw.players) {
      const teams = raw.team
      let players = raw.players
      let playerKeys = []

      Object.keys(players).forEach((key) => {
        players[key] = FootballPlayer.createFromJson(key, players[key])
        Object.keys(teams).forEach((teamKey) => {
          if (teams[teamKey].players.indexOf(key) > -1) {
            players[key].teamKey = teamKey
          }
        })
      })

      let credits = fantasy.credits
      credits.forEach((point) => {
        if (players[point.player_key]) {
          playerKeys.push(point.player_key)
          players[point.player_key].fantasyCredit = FootballPlayerFantasyCredit.createFromJson(point)
        } else {
          console.log('Player missing', point.player_key)
        }
      })

      const grouped = this.groupPlayersByRole(playerKeys, players)
      const variation = resp.data.data.variation
      return { grouped, players, playerKeys, teams, variation }
    }

    return null
  }
  parseCreditVariationResponse (resp) {
    const raw = resp.data.data.fantasy
    const fantasy = raw.fantasy
    if (raw.players) {
      let players = raw.players
      let playerKeys = []

      Object.keys(players).forEach((key) => {
        players[key] = FootballPlayer.createFromJson(key, players[key])
      })

      let credits = fantasy.credits
      credits.forEach((point) => {
        if (players[point.player_key]) {
          playerKeys.push(point.player_key)
          players[point.player_key].fantasyCredit = FootballPlayerFantasyCredit.createFromJson(point)
        } else {
          console.log('Player missing', point.player_key)
        }
      })

      const grouped = this.groupPlayersByRole(playerKeys, players)
      const teams = raw.teams
      const variation = resp.data.data.variation
      return { grouped, players, playerKeys, teams, variation }
    }

    return null
  }
}
